import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useConfirm } from "../../components/ConfirmProvider";

/**
 * Card mare cu to-do list pentru proiectul special MAGAZIA.
 * - Admin + planificator (PROIECTE.edit) pot adăuga items.
 * - Toți cu PROIECTE.view pot schimba statusul (3 stări).
 * - Doar admin poate edita titlul/descrierea sau șterge.
 */
const STATUS_OPTIONS = [
  { value: "nefacut", label: "🔴 Nefăcut", className: "bg-rose-50 border-rose-300 text-rose-800" },
  { value: "in_lucru", label: "🟡 În lucru", className: "bg-amber-50 border-amber-300 text-amber-800" },
  { value: "finalizat", label: "🟢 Finalizat", className: "bg-emerald-50 border-emerald-300 text-emerald-800" },
];

export default function MagaziaCard({ user, canEdit, onChanged }) {
  const confirm = useConfirm();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/magazia/todos");
      setTodos(r.data || []);
    } catch {
      toast.error("Eroare la încărcarea to-do list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addTodo = async () => {
    if (!newTitle.trim()) { toast.error("Titlul este obligatoriu"); return; }
    try {
      await api.post("/magazia/todos", { title: newTitle.trim(), description: newDesc.trim() || null });
      toast.success("✓ Item adăugat");
      setNewTitle(""); setNewDesc(""); setShowAdd(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare");
    }
  };

  const changeStatus = async (todo, newStatus) => {
    if (todo.status === newStatus) return;
    try {
      await api.patch(`/magazia/todos/${todo.id}/status`, { status: newStatus });
      toast.success(`Status actualizat: ${STATUS_OPTIONS.find((s) => s.value === newStatus)?.label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la schimbarea statusului");
    }
  };

  const saveEdit = async () => {
    if (!editTitle.trim()) { toast.error("Titlul este obligatoriu"); return; }
    try {
      await api.put(`/magazia/todos/${editingId}`, { title: editTitle.trim(), description: editDesc.trim() || null });
      toast.success("✓ Salvat");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la editare");
    }
  };

  const deleteTodo = async (todo) => {
    const ok = await confirm({
      title: "Șterge item",
      body: `Ești sigur că vrei să ștergi „${todo.title}"?`,
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/magazia/todos/${todo.id}`);
      toast.success("Item șters");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  // Sortare: nefacut > in_lucru > finalizat, apoi după created_at desc
  const orderRank = { nefacut: 0, in_lucru: 1, finalizat: 2 };
  const sorted = [...todos].sort((a, b) => {
    const ra = orderRank[a.status] ?? 99;
    const rb = orderRank[b.status] ?? 99;
    if (ra !== rb) return ra - rb;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  const counts = todos.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  return (
    <div
      className="rounded-lg border-2 border-zinc-300 bg-gradient-to-br from-zinc-50 to-zinc-100 shadow-sm"
      data-testid="magazia-card"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b-2 border-zinc-300">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 font-bold text-sm hover:text-zinc-700"
          data-testid="magazia-toggle-btn"
        >
          <span className="text-base">🛠</span>
          <span>MAGAZIA — To-Do List</span>
          <span className="text-xs font-mono text-zinc-500">({todos.length})</span>
          <span className="text-zinc-400">{collapsed ? "▸" : "▾"}</span>
        </button>
        <div className="flex items-center gap-1.5 text-[10px] font-bold">
          {counts.nefacut > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">🔴 {counts.nefacut}</span>}
          {counts.in_lucru > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">🟡 {counts.in_lucru}</span>}
          {counts.finalizat > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">🟢 {counts.finalizat}</span>}
          {canEdit && !showAdd && !collapsed && (
            <Button size="sm" onClick={() => setShowAdd(true)} className="h-7 ml-2 bg-zinc-700 hover:bg-zinc-800 text-white text-xs" data-testid="magazia-add-btn">
              ➕ Adaugă item
            </Button>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-2">
          {showAdd && (
            <div className="rounded-md border-2 border-zinc-400 bg-white p-3 space-y-2" data-testid="magazia-add-form">
              <Input
                placeholder="ex: Sortează șuruburile mari · 4 cutii roșii"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="h-9 text-sm"
                data-testid="magazia-new-title"
                autoFocus
              />
              <Textarea
                placeholder="Descriere / detalii (opțional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={2}
                className="text-xs"
                data-testid="magazia-new-desc"
              />
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => { setShowAdd(false); setNewTitle(""); setNewDesc(""); }}>Anulează</Button>
                <Button size="sm" onClick={addTodo} disabled={!newTitle.trim()} className="bg-zinc-700 hover:bg-zinc-800 text-white" data-testid="magazia-new-save">
                  ✓ Adaugă
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center text-xs text-zinc-400 italic py-4">Se încarcă…</div>
          ) : sorted.length === 0 ? (
            <div className="text-center text-xs text-zinc-400 italic py-4">
              Niciun item în to-do list.{canEdit ? ' Apasă „➕ Adaugă item" mai sus.' : ""}
            </div>
          ) : (
            sorted.map((t) => {
              const cur = STATUS_OPTIONS.find((s) => s.value === t.status) || STATUS_OPTIONS[0];
              const isEditing = editingId === t.id;
              return (
                <div
                  key={t.id}
                  className={`rounded border ${cur.className} p-2.5 transition-shadow hover:shadow-sm`}
                  data-testid={`magazia-todo-${t.id}`}
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="h-8 text-sm" data-testid={`magazia-edit-title-${t.id}`} />
                      <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} className="text-xs" data-testid={`magazia-edit-desc-${t.id}`} />
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Anulează</Button>
                        <Button size="sm" onClick={saveEdit} className="bg-zinc-700 hover:bg-zinc-800 text-white" data-testid={`magazia-edit-save-${t.id}`}>💾 Salvează</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{t.title}</div>
                        {t.description && <div className="text-[11px] text-zinc-600 mt-0.5 whitespace-pre-line">{t.description}</div>}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {STATUS_OPTIONS.map((s) => (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => changeStatus(t, s.value)}
                              className={`text-[10px] px-2 py-0.5 rounded border font-bold transition-all ${
                                t.status === s.value
                                  ? `${s.className} ring-2 ring-offset-1 ring-zinc-400`
                                  : "border-zinc-200 bg-white text-zinc-500 hover:border-zinc-400 hover:text-zinc-700"
                              }`}
                              data-testid={`magazia-status-${t.id}-${s.value}`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1">
                          Adăugat de <span className="font-semibold">{t.created_by_name}</span> · {(t.created_at || "").slice(0, 10)}
                          {t.status_changed_at && t.status_changed_by_name && (
                            <> · ultima schimbare de <span className="font-semibold">{t.status_changed_by_name}</span> ({(t.status_changed_at || "").slice(0, 16).replace("T", " ")})</>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => { setEditingId(t.id); setEditTitle(t.title); setEditDesc(t.description || ""); }}
                            className="text-[10px] px-2 py-0.5 rounded border border-zinc-300 bg-white hover:bg-zinc-100"
                            data-testid={`magazia-edit-${t.id}`}
                            title="Editează (doar admin)"
                          >
                            ✎ Editează
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteTodo(t)}
                            className="text-[10px] px-2 py-0.5 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50"
                            data-testid={`magazia-delete-${t.id}`}
                            title="Șterge (doar admin)"
                          >
                            🗑 Șterge
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
